import * as THREE from 'three';
import { cameraControls, getActiveCamera, getSpotlight, mainCameraInitPosition, scene } from './Initial3DSetup';
import { planets } from './Planets';
//import { loadingManager } from '../JS/GlobalFunctions';
import gsap from 'gsap';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import gateGLB from '../../../Assets/GLTF/Gate_Animated.glb';
import { updateSpotlightTarget } from './Animate';
import { setIsAnimating } from './MainScene';
import { getLoadingManager } from '../../Utils/loadManager';
import { flyByPoint, launchEase, OPENING_MOVE_DURATION } from '../../Utils/flyBy';
import { DOOR_LEAF_NAMES, doorAimQuaternion, doorwayCentre } from '../../Utils/lobbyDoor';
import { disableLobbyWalk, enableLobbyWalk } from './LobbyWalkControls';
// The one chrome element the scene imports rather than reaches for by id. The Skip and the blackout
// above are a class toggled on a plain DOM node, which is enough when the question is only whether
// something is drawn — but the Legend's list is two different lists, and a class cannot say which.
import { setLegend } from '../../../Components/Legend';


export const doors = new THREE.Group();

// Shared with every other loader in the opening scene, so the loading overlay
// reports one honest percentage for the whole thing.
const manager = getLoadingManager();
const clock = new THREE.Clock();

let gate = new THREE.Object3D();

let target
export let timeline;

// The button that cuts the opening move short, and the black it cuts through. Both live in
// App.js as plain DOM inside #canvasEl; this module only ever toggles their classes, the way
// LobbyWalkControls drives the reticle.
const SKIP_BUTTON_ID = 'skipMove';
const SKIP_SHOWN_CLASS = 'skipMove--shown';
const BLACKOUT_ID = 'moveBlackout';
const BLACKOUT_BLACK_CLASS = 'moveBlackout--black';

/** Keep in step with .moveBlackout's transition in App.css. */
export const SKIP_FADE_MS = 400;

// The move is a tween rather than a timeline child (see openingMove), so the timeline cannot
// stop it — this handle is what does. Held from the moment the flight starts.
let moveTween = null;

// The doorway's flash, held for the same kind of reason: they are added to the scene rather
// than to the gate (see addPointLights), so the gate's own teardown does not reach them and
// nothing else would ever take them away.
let doorwayLights = [];

// Two separate questions, and both have to be asked before a click counts. `offered` says the
// flight is running and the button is on screen; `taken` says this click is the one that ends
// it. Without the second, a second click during the fade would land the skip twice.
let skipOffered = false;
let skipTaken = false;

// Whether the move has ended. Two ways into the landing, and they can overlap — see closeDoors.
let landed = false;

/** The gate's two leaves, or nothing if the gate is not up yet. */
function doorLeaves() {
    return DOOR_LEAF_NAMES.map((name) => gate.getObjectByName(name)).filter(Boolean);
}

function skipButton() {
    return document.getElementById(SKIP_BUTTON_ID);
}

function blackout() {
    return document.getElementById(BLACKOUT_ID);
}

/**
 * Puts the Skip button on screen. Called as the flight starts rather than when the doors begin,
 * so the button is only offering the move that is actually happening.
 */
function offerSkip() {
    const button = skipButton();

    if (!button || skipOffered) return;

    skipOffered = true;

    // A real button: it takes focus, so it can be reached by keyboard as well as by the mouse.
    // Focus is not moved to it — the walk's pointer lock is released by now and a button that
    // grabs focus under a moving camera would be startling — but nothing stops Tab reaching it.
    button.classList.add(SKIP_SHOWN_CLASS);
}

/**
 * Takes the button back off screen, and out of the tab order with it. The class does both: the
 * `hidden` attribute would not, because any author `display` beats it.
 */
function hideSkip() {
    const button = skipButton();

    skipOffered = false;

    if (button) button.classList.remove(SKIP_SHOWN_CLASS);
}

function clearBlackout() {
    const layer = blackout();

    if (layer) layer.classList.remove(BLACKOUT_BLACK_CLASS);
}

/**
 * The Skip. One press, and the opening move is over.
 *
 * The flight tween is killed here rather than at the cut 400ms later, and that is the whole
 * reason this is not simply a `setTimeout`. `gsap.to` is a root-level tween — the timeline
 * holds a callback that starts it, not the tween itself — so killing `timeline` would leave it
 * running. Left running it could reach its own landing inside the fade and call `closeDoors`
 * mid-black, and `closeDoors` disposes the gate: called twice, the second call would walk a
 * already-disposed subtree. Killing at the press leaves no flight to land, so the landing below
 * is the only one there is.
 */
export function skipMove() {
    if (!skipOffered || skipTaken) return;

    skipTaken = true;

    if (moveTween) moveTween.kill();

    const layer = blackout();

    // The button is not taken away here. It sits under the blackout, so the fade swallows it
    // on the way in, and closeDoors is then the single place any move ends — the flight's
    // landing and the skip's alike. "Once at the home position, remove it" is that one line.
    if (!layer) {
        landTheSkip();

        return;
    }

    layer.classList.add(BLACKOUT_BLACK_CLASS);
    window.setTimeout(landTheSkip, SKIP_FADE_MS);
}

/**
 * The camera, put where the flight would have put it — and the landing run the same way.
 *
 * The position is set and the orientation is not. OrbitControls.update ends with
 * `this.object.lookAt(this.target)`, and closeDoors points that target at the origin, so the
 * orientation is derived from this position and the sun the moment the controls resume. An
 * explicit quaternion here would be overwritten by that line with the value it would have
 * held anyway — which is the invariant flyBy.test.js pins: the flight's own last frame aims
 * at the sun from the home diagonal, and this does too.
 */
function landTheSkip() {
    if (timeline) timeline.kill();

    const camera = getActiveCamera();

    camera.position.copy(mainCameraInitPosition);

    closeDoors();
}

export function loadLobbyContainer() {

    const il = new GLTFLoader(manager);

    il.load(gateGLB, (obj => {

        gate = obj.scene.children[0];

        gate.position.copy(getActiveCamera().position);
        gate.position.z -= 2;
 
     
        const targetGeo = new THREE.BoxGeometry(1, 1, 0.1, 1, 1, 1);
        const targetMat = new THREE.MeshBasicMaterial({ transparent: true, opacity: 0 });
        target = new THREE.Mesh(targetGeo, targetMat);
        target.name = 'doorTarget';
        gate.position.z -= 3;
        gate.name = 'gate';
        target.position.copy(gate.position);
        target.position.z += 0.15;
        target.position.y -= 0.125;
        target.layers.set(1);

        scene.add(target);
        scene.add(gate);

        //flash light
        const o = new THREE.Object3D();
        getActiveCamera().add(o);
        o.position.set(0, 0, -1);
        o.userData.spotlightIntensity = 10;
        doorwayLights = addPointLights(gate);

        //getActiveCamera().lookAt(target);
        updateSpotlightTarget(o);

        const t = new THREE.Vector3().copy(getActiveCamera().position);
        t.z -= 0.1;
        cameraControls.target = t;
        cameraControls.screenSpacePanning = false; // Disable screen-space panning

        // The gate is here and the camera is waiting outside it, which is the lobby.
        // Walking is open from now until the doors do.
        enableLobbyWalk(gate);

        // Wired once, here, rather than per move: the button is authored in App.js and outlives
        // every move, so it is attached at module init and left attached. The GLB has loaded by
        // now, which is well after React has committed the DOM the button lives in.
        const button = skipButton();

        if (button) button.addEventListener('click', skipMove);
    }));

    /* SUPERSEDED — the shared manager (../../Utils/loadManager) drives the
       loading overlay now, and assigning these handlers would clobber it.
       Kept only as a record of the old #loading element approach.

    let perc;

    manager.onLoad = function () {
        const el = document.querySelector('#loading');
        el.classList.add('hidden');       
    };

    manager.onProgress = function (url, itemsLoaded, itemsTotal) {
        perc = getPercentage();
        const el = document.querySelector('#loading');
        const newperc = perc + Math.trunc((itemsLoaded / itemsTotal * 100) / 2);
        const prog = 'Loading 3D Models... ' + newperc + '%.';       
        el.innerText = prog;
    }; */
}

export function openDoors() {
    // The way out is the one thing the lobby asks the player to find, so the list telling them to
    // find it goes the moment they do. Nothing opens it again from here: the landing does, below.
    setLegend(null);

    const camera = getActiveCamera();

    let startOrientation = camera.quaternion.clone();

    // The aim the turn below slerps to, taken from where the doorway actually is. The
    // `doorTarget` mesh cannot supply it: it is placed with position.copy and its quaternion is
    // never written, so it is the identity, and slerping to the identity aims the camera down
    // the world's -Z — which is the doorway only from the one stance the camera arrives on. The
    // walk is free to leave it two units off that axis and a unit and a half outside the doors,
    // where the doorway is 53° away, and the turn finishes with it at the edge of the frame.
    //
    // The old aim is kept as the fallback, so a gate whose leaves cannot be measured turns the
    // camera the way it always did rather than to some default orientation.
    const aimed = doorAimQuaternion(camera.position, doorwayCentre(doorLeaves()), camera.up);
    let targetOrientation = aimed || target.quaternion.clone().normalize();
    let doorOpenLeft, doorOpenRight, move;
    setIsAnimating(true);

    // The whole opening move owns the camera from here — flight out, the circuit, the
    // landing. A drag part-way through would fight the tweens for it, and the move is now
    // long enough that one is likely. closeDoors hands control back.
    cameraControls.enabled = false;

    // Same reason: a key still held when the button is pressed would have the walk and
    // the tweens fighting over the camera for the whole move.
    disableLobbyWalk();

    // The whole opening move is one path and one tween: out of the gate, down onto the
    // system, once round the sun, landing on the home. The move used to be two, and both of
    // their eases came to rest where they met — the camera stopped dead out on the diagonal
    // and set off again facing almost the opposite way. One tween has no meeting point to
    // get that wrong at, and the shaping of the route still lives entirely in the path.
    //
    // The ramp is flyBy.js's own `launchEase`, applied to this tween's linear progress by
    // hand, and the tween is pinned to `ease: 'none'` so it stays honestly linear underneath.
    //
    // That pin is the point. This tween used to carry `ease: 'power2.inOut'` and the callback
    // read `this.progress()` — and gsap's ease moves `tween.ratio`, never `tween.progress()`,
    // which stays linear whatever ease is set. So the ease was read by nobody, the tween ran
    // linear all along, and the slower start it had been added for never happened. Anything
    // that shapes the camera's speed belongs in launchEase, where a test can hold it.
    function openingMove() {
        // Offered with the move it skips rather than with the doors: until this runs the camera
        // has not started travelling, so there is nothing yet to skip.
        offerSkip();

        moveTween = gsap.to({}, {
            duration: OPENING_MOVE_DURATION,
            ease: 'none',
            onUpdate: function () {
                const progress = launchEase(this.progress());
                const point = flyByPoint(progress);

                camera.position.set(point.x, point.y, point.z);

                // The doors are a window onto the sun, so the sun stays pinned to the
                // centre of frame the whole way rather than tweening to a fixed yaw.
                // Everything the camera aims at sits in the y = 0 plane, so this comes
                // out as a clean yaw sweep that lands on the resting orientation
                // instead of snapping onto it when the controls take over.
                camera.lookAt(0, 0, 0);
                carryPlanetLabels(progress);
            }
        });

        // The handle is held because this tween is not a child of the timeline — the timeline
        // holds this function, which creates the tween at the root — so `timeline.kill()` does
        // not reach it. skipMove is what kills it.
        moveTween.then(closeDoors);
    }

    // Animate.js skips the labels while a doors animation is running, so the move has to
    // carry them itself or they freeze mid-move.
    function carryPlanetLabels(progress) {
        planets.traverse(obj => {
            if (obj.isPlanetLabel) {
                const from = obj.quaternion.clone();
                const to = camera.quaternion.clone();
                obj.quaternion.copy(from).slerp(to, progress);
            }
        });
    }

    const lookAtDoor = gsap.to({}, {
        duration: 1,
        onUpdate: function () {
            camera.quaternion.copy(startOrientation).slerp(targetOrientation, this.progress());
        }
    });


    timeline = gsap.timeline();


    gate.children.forEach(door => {
        if (door.name === 'Door_left') {
            move = door.position.x - 2;
            openDoor(door, move, true);
        } else if (door.name === 'Door_right') {
            move = door.position.x + 2;
            openDoor(door, move, false);
        }
    });

    timeline.add(lookAtDoor).add(doorOpenLeft, '-=0.25').add(doorOpenRight, '-=2').add(openingMove);

    function openDoor(door, move, left) {
        if (left) {
            doorOpenLeft = gsap.to(door.position, {
                x: move,
                duration: 2
            });
        } else {
            doorOpenRight = gsap.to(door.position, {
                x: move,
                duration: 2
            });
        }
    }

    timeline.play();
}



/**
 * Frees the GPU memory the gate was holding.
 *
 * scene.remove() detaches the gate from the graph and frees nothing: each geometry keeps
 * its vertex buffers and every material keeps its textures, resident on the GPU for the
 * lifetime of the page. This is a large thing to keep for a gate that is never drawn
 * again. Measured from the GLB: 135,509,800 bytes (129 MB), of which 127,027,576 is image
 * data — 13 textures over 10 images — on 88 primitives across 254 mesh nodes and 9
 * materials. The decoded GPU copies are not smaller than those encoded bytes. The main
 * scene then loads a cube map and the planet textures on top of it, and this app is meant
 * to run where GPU memory is the scarce resource.
 *
 * It is safe only because the lobby is a one-way trip: MainScene's `doorsOpen` is set true
 * on the DoorButton click and never set back, `loadLobbyContainer` runs once per page load
 * (App.js waits for `.appContainer` and calls initMainScene once — no StrictMode, no route
 * that unmounts the scene), and nothing re-adds the gate or re-enters the lobby. Getting
 * back to a disposed gate would draw it black or throw. If a way back is ever added, this
 * call is what has to go — or the gate has to be reloaded rather than kept.
 *
 * Nothing outside the gate's own subtree is touched. The LoadingManager is the scene-wide
 * one (Utils/loadManager) and is none of this function's business; `target` is a sibling in
 * the scene, not a child of the gate, and is still the spotlight's target after this; and
 * the four point lights addPointLights made were added to the scene rather than to the
 * gate, so they are outside this subtree and this function leaves them alone on purpose —
 * `closeDoors` takes them away itself, immediately after this call, because nothing else
 * would ever reach them. The gate's textures come from its
 * own GLB load and nothing else can reach them — THREE.Cache is not enabled anywhere, so
 * no other loader shares them.
 */
function disposeGateResources(root) {
    const geometries = new Set();
    const materials = new Set();

    // 254 mesh nodes over 88 primitives and only 9 materials, so the same geometry and the
    // same material are reached over and over. The sets are what make this one dispose per
    // resource rather than one per mesh that uses it.
    root.traverse((child) => {
        if (child.geometry) geometries.add(child.geometry);

        // A mesh carries one material or an array of them. This asset never takes the array
        // branch — every glTF mesh here is one or two primitives, and GLTFLoader gives each
        // primitive its own Mesh with a single material — but the walk should not depend on
        // which loader built the object, so both shapes are read.
        if (child.material) {
            const list = Array.isArray(child.material) ? child.material : [child.material];
            list.forEach((material) => materials.add(material));
        }
    });

    geometries.forEach((geometry) => geometry.dispose());

    materials.forEach((material) => {
        // A material's textures hang off it as named properties — map, normalMap,
        // roughnessMap, metalnessMap, aoMap, emissiveMap, alphaMap, envMap and the rest.
        // Object.values picks up the non-texture properties too (colour, opacity, the
        // material's own id, its uuid), so each one is tested before it is disposed.
        // The nine materials here hold thirteen textures between them, and each material
        // is disposed once, so no texture is disposed twice.
        Object.values(material).forEach((value) => {
            if (value && value.isTexture) value.dispose();
        });

        material.dispose();
    });
}

function closeDoors() {
    // Once, ever. There are two ways into this landing and they can overlap: a Skip pressed on
    // the flight's last frame kills a tween that has already finished, so the tween's own
    // landing is still queued behind the press and runs, and then the skip's landing runs 400ms
    // behind that. Both would reach the teardown below, and it disposes the gate.
    if (landed) return;

    landed = true;

    // Both landings arrive here — the flight's own and the skip's — so both end the same way,
    // which is what "once at the home position, remove it" asks for. The black is cleared
    // rather than faded out by hand: its own transition is what brings the screen back.
    hideSkip();
    clearBlackout();
    moveTween = null;

    // Both landings arrive on the same screen — the solar system, with nothing left to walk — so
    // the list that describes *that* is opened here rather than at either landing, and the skip and
    // the flight's own ending get it for free. It opens behind the black and is revealed as the
    // fade above brings the screen back, which is the whole of the transition.
    setLegend('solar');

    setIsAnimating(false);
    scene.remove(gate);
    disposeGateResources(gate);

    // And the doorway's flash, which the gate's teardown deliberately does not reach (they are
    // the scene's children, not the gate's). They have nothing left to light — the lobby is a
    // one-way trip — and left in the scene they are four unlimited lights every lit material
    // in the solar system goes on paying for, on every pixel, for the rest of the page.
    doorwayLights.forEach((light) => {
        scene.remove(light);
        light.dispose();
    });
    doorwayLights = [];
    cameraControls.target = new THREE.Vector3();
    cameraControls.enabled = true;

    const spotlight = getSpotlight();
    spotlight.target = target;
    spotlight.distance = 100;
    spotlight.intensity = 6;
    spotlight.decay = 1;
    spotlight.penumbra = 1;
    spotlight.focus = 1;
    spotlight.angle = 0.25;

    cameraControls.update(clock.getDelta());
}

/**
 * How far the doorway's flash reaches, in scene units. The one number in this feature: raise it
 * and the flash spreads into the room, lower it and the doors go dim at their own corners.
 */
const DOORWAY_LIGHT_DISTANCE = 12;

function addPointLights(gate) {
    const pointlights = [new THREE.PointLight(), new THREE.PointLight(), new THREE.PointLight(), new THREE.PointLight()];

    pointlights.forEach((light, i) => {
        light.position.copy(gate.position);
        light.position.y += 1.15;
        light.position.z += 0.25;
        if (i % 2 === 0) {
            light.position.x += 0.85;
        } else {
            light.position.x -= 0.85;
        }

        if (i > 2) {
            light.position.z += 0.25;
        } else {
            light.position.z -= 0.25;
        }

        light.intensity = 0.25;
        // Bounded, and this is the whole of the fix. `THREE.PointLight`'s default `distance` is
        // `0`, which means *no limit* — and an unlimited light is not a light that reaches
        // further, it is a light every lit fragment in the scene is lit by. Four of them, on
        // every lit material, for the life of the page, with a cost that scales with how much
        // of the frame the lit thing covers. These light a doorway five units in front of the
        // camera; twelve units covers the doorway, its leaves (they part ±2 on x) and the room
        // the walk is allowed in, and stops well short of everything else.
        light.distance = DOORWAY_LIGHT_DISTANCE;
        light.color = new THREE.Color('green')
        scene.add(light);
    })

    return pointlights;
}

